import { useEffect, useRef } from 'react';
import { formatPrice, type PlanStep, type TransferPlan } from '@fantasy/types';
import { Term } from './Term.tsx';

interface TransferPlanViewProps {
  plan: TransferPlan;
}

/**
 * The multi-week companion to `AnalysisView`'s single-gameweek verdict: what
 * to do this week, what to bank, and what's worth doing three (or more)
 * gameweeks out. Every number here is deterministic beam-search output from
 * `planTransfers` — this component only lays it out, it doesn't judge it.
 */
export function TransferPlanView({ plan }: TransferPlanViewProps) {
  const { warnings, totalPointsDelta, horizon } = plan;
  const steps = [...plan.steps].sort((a, b) => a.gameweek - b.gameweek);
  const allHold = steps.every((step) => step.transfers.length === 0);

  // Same reasoning as AnalysisView: this renders below a squad that can run a
  // full screen tall, so a result that just silently appears is easy to miss
  // without knowing to scroll. Re-fires on every fresh plan, not just the
  // first, since a re-run is exactly as easy to lose track of.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [plan]);

  return (
    <section className="panel panel--accent-2 rise-in" aria-labelledby="plan-heading">
      <h3 id="plan-heading" ref={headingRef} tabIndex={-1}>
        The transfer plan
      </h3>
      <p className="hint">
        Over the next {horizon} gameweek{horizon === 1 ? '' : 's'}
      </p>

      {warnings.length > 0 && (
        <ul className="notices">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}

      <dl className="callout">
        <dt>Projected gain</dt>
        <dd>
          <strong>{totalPointsDelta > 0 ? `+${totalPointsDelta.toFixed(1)}` : totalPointsDelta.toFixed(1)}</strong>{' '}
          points over {horizon} gameweek{horizon === 1 ? '' : 's'}, versus doing nothing
        </dd>
      </dl>

      {allHold ? (
        <p className="callout-detail">
          Holding your current squad is the best available plan over this horizon — no transfer beats it.
        </p>
      ) : (
        <ol className="plan-timeline list-reset">
          {steps.map((step) => (
            <PlanStepItem key={step.gameweek} step={step} />
          ))}
        </ol>
      )}
    </section>
  );
}

function PlanStepItem({ step }: { step: PlanStep }) {
  return (
    <li className="plan-step">
      <div className="plan-step-head">
        <span className="plan-step-gw">Gameweek {step.gameweek}</span>
        <span className="text-faint">
          {step.pointsDelta >= 0 ? '+' : ''}
          {step.pointsDelta.toFixed(1)} vs holding
        </span>
      </div>

      {step.transfers.length === 0 ? (
        <p className="text-dim">Hold — bank the transfer</p>
      ) : (
        <ul className="list-reset">
          {step.transfers.map((transfer) => (
            <li key={`${transfer.out}-${transfer.in}`} className="plan-transfer">
              <strong>{transfer.out}</strong> → <strong>{transfer.in}</strong>{' '}
              <span className="text-faint">
                ({transfer.netCost >= 0 ? '' : '+'}
                {formatPrice(Math.abs(transfer.netCost))})
              </span>
            </li>
          ))}
        </ul>
      )}

      {step.pointsHit > 0 && <p className="text-warn">
          Costs a {step.pointsHit}-point <Term term="hit">hit</Term>.
        </p>}

      <p className="hint hint--inline">
        Captain <strong>{step.captain}</strong> · projected <strong>{step.projectedPoints.toFixed(1)}</strong>{' '}
        points
      </p>
    </li>
  );
}
